/**
 * Device List Data Processing Functions
 * Handles grouping and statistics calculation for device list data
 */

import type {
  DeviceListItem,
  DeviceGroupData,
  DeviceListStatistics
} from './types';

/**
 * Process device list data for grouping and statistics
 * Handles the complex data transformation in API layer
 * @param devices - Raw device list from API
 * @returns Processed groups and statistics
 */
export function processDeviceListData(devices: DeviceListItem[]): {
  groups: DeviceGroupData[];
  statistics: DeviceListStatistics;
} {
  // Calculate statistics
  const statistics = calculateDeviceStatistics(devices);
  
  // Group devices
  const groups = groupDevicesByGroup(devices);
  
  return { groups, statistics };
}

/**
 * Group devices by their group information
 * @param devices - Array of device list items
 * @returns Array of device groups
 */
export function groupDevicesByGroup(devices: DeviceListItem[]): DeviceGroupData[] {
  const groupMap = new Map<string, DeviceListItem[]>();
  
  // Group devices by group name
  devices.forEach(item => {
    const groupName = item.groupName || 'Ungrouped';
    const groupId = item.groupManagementID || 'ungrouped';
    
    if (!groupMap.has(groupId)) {
      groupMap.set(groupId, []);
    }
    groupMap.get(groupId)!.push(item);
  });
  
  // Convert to group data array
  const groups: DeviceGroupData[] = [];
  
  groupMap.forEach((devices, groupId) => {
    const firstDevice = devices[0];
    groups.push({
      groupId,
      groupName: firstDevice.groupName || 'Ungrouped',
      groupRegDate: firstDevice.groupRegDT,
      devices,
      isExpanded: groupId === 'ungrouped' // Default expand ungrouped
    });
  });
  
  // Sort groups: ungrouped first, then alphabetically
  groups.sort((a, b) => {
    if (a.groupId === 'ungrouped') return -1;
    if (b.groupId === 'ungrouped') return 1;
    return a.groupName.localeCompare(b.groupName);
  });
  
  return groups;
}

/**
 * Calculate device statistics
 * @param devices - Array of device list items
 * @returns Device statistics
 */
export function calculateDeviceStatistics(devices: DeviceListItem[]): DeviceListStatistics {
  const total = devices.length;
  const online = devices.filter(item => item.device.active === 'on').length;
  const offline = total - online;
  const grouped = devices.filter(item => !!item.groupManagementID).length;
  const ungrouped = total - grouped;
  const videoSharing = devices.filter(item => item.device.share_video === 'on').length;
  const normalMode = devices.filter(item => item.device.mode === '0').length;
  const parkingMode = devices.filter(item => item.device.mode === '1').length;
  const wifiOnly = devices.filter(item => item.device.reg_category === 'wifi').length;
  const cloudEnabled = devices.filter(item => item.device.reg_category === 'cloud').length;
  const withBattery = devices.filter(item => !!item.device.battery).length;

  return {
    total,
    online,
    offline,
    grouped,
    ungrouped,
    videoSharing,
    normalMode,
    parkingMode,
    wifiOnly,
    cloudEnabled,
    withBattery
  };
}